import { motion } from "framer-motion";

/* Real brand marks (Simple Icons, in /public/logos) tinted monochrome via CSS mask.
   Items without a safely-available official logo are shown as clean text wordmarks — never fabricated. */

const L = (slug, name, accent = false) => ({ slug, name, accent });
const T = (name) => ({ name, text: true });

/* Brands with a vibrant official full-color mark that reads well on black (rendered as <img>).
   Inherently dark/mono marks (github, notion, vercel, openai, anthropic, square) stay as light-tinted masks. */
const COLOR_SET = new Set([
  "stripe", "paypal", "visa", "mastercard", "amazonwebservices", "amazons3", "cloudflare", "digitalocean",
  "docker", "kubernetes", "mongodb", "postgresql", "redis", "gitlab", "linear", "jira", "salesforce",
  "hubspot", "twilio", "slack", "googlecalendar", "mailchimp", "intercom", "asana", "zendesk",
  "google", "googleworkspace", "microsoft", "sendgrid",
]);

/* On-black brand colors for the tinted-mask fallback set */
const COLOR = {
  square: "#E8E8EA", quickbooks: "#2CA01C", vercel: "#F4F4F5",
  openai: "#10A37F", anthropic: "#D97757", github: "#F4F4F5", zapier: "#FF6A33", notion: "#F4F4F5",
};

const GROUPS = [
  {
    label: "Payments & Finance",
    items: [L("stripe", "Stripe", true), L("sendgrid", "SendGrid"), L("paypal", "PayPal"), L("visa", "Visa"), L("mastercard", "Mastercard"), L("square", "Square"), L("quickbooks", "QuickBooks")],
    dir: "left",
  },
  {
    label: "Cloud & Infrastructure",
    items: [L("amazonwebservices", "AWS"), L("amazons3", "Amazon S3"), L("cloudflare", "Cloudflare", true), L("digitalocean", "DigitalOcean"), L("vercel", "Vercel"), L("docker", "Docker"), L("kubernetes", "Kubernetes"), L("mongodb", "MongoDB"), L("postgresql", "PostgreSQL"), L("redis", "Redis")],
    dir: "right",
  },
  {
    label: "AI & Developer Tools",
    items: [L("openai", "OpenAI", true), L("anthropic", "Anthropic"), L("github", "GitHub"), L("gitlab", "GitLab"), L("zapier", "Zapier"), L("linear", "Linear"), L("jira", "Jira")],
    dir: "left",
  },
  {
    label: "CRM, Comms & Operations",
    items: [L("salesforce", "Salesforce"), L("hubspot", "HubSpot", true), L("twilio", "Twilio"), L("slack", "Slack"), L("notion", "Notion"), L("calendly", "Calendly"), L("googlecalendar", "Google Calendar"), L("mailchimp", "Mailchimp"), L("intercom", "Intercom"), L("zendesk", "Zendesk"), L("asana", "Asana"), L("google", "Google"), L("googleworkspace", "Google Workspace"), L("microsoft", "Microsoft"), T("Microsoft 365")],
    dir: "right",
  },
  {
    label: "Government & Business Infrastructure",
    items: [T("IRS / EIN"), T("U.S. Government"), T("Secretary of State"), T("Business Registration"), T("EIN Filing")],
    dir: "left",
  },
];

function Item({ item }) {
  if (item.text) {
    return (
      <span className="shrink-0 font-heading text-base md:text-lg tracking-tight text-zinc-500 hover:text-zinc-200 transition-colors duration-300 whitespace-nowrap">
        {item.name}
      </span>
    );
  }
  if (COLOR_SET.has(item.slug)) {
    return (
      <img
        src={`/logos/color/${item.slug}.svg`}
        alt={item.name}
        title={item.name}
        loading="lazy"
        className="shrink-0 h-6 md:h-7 w-auto max-w-[132px] object-contain opacity-90 hover:opacity-100 transition-opacity duration-300"
      />
    );
  }
  return (
    <span
      role="img"
      aria-label={item.name}
      title={item.name}
      className="logo-mark shrink-0 h-6 md:h-7 w-[104px] md:w-[120px] opacity-85 hover:opacity-100 transition-opacity duration-300"
      style={{
        WebkitMaskImage: `url(/logos/${item.slug}.svg)`,
        maskImage: `url(/logos/${item.slug}.svg)`,
        backgroundColor: COLOR[item.slug] || "#D4D4D8",
      }}
    />
  );
}

const Track = ({ items }) => (
  <div className="flex items-center gap-12 md:gap-16 pr-12 md:pr-16">
    {items.map((item, i) => <Item key={`${item.name}-${i}`} item={item} />)}
  </div>
);

function Row({ group, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 34 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.75, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="border-t border-[#131313] py-6 md:py-7"
    >
      <div className="max-w-[1560px] mx-auto px-6 md:px-10 lg:px-16 mb-4">
        <span className="mono-label text-zinc-600">{group.label}</span>
      </div>
      <div className="marquee-hover marquee-fade overflow-hidden">
        <div className={`${group.dir === "right" ? "animate-marquee-rev" : "animate-marquee"} flex w-max`}>
          <Track items={group.items} />
          <Track items={group.items} />
        </div>
      </div>
    </motion.div>
  );
}

export function Integrations() {
  return (
    <section className="py-20 md:py-28 border-t border-[#131313]">
      <div className="max-w-[1560px] mx-auto px-6 md:px-10 lg:px-16 mb-12 md:mb-16">
        <motion.p
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mono-label text-zinc-600 mb-4"
        >
          Integrations &amp; Technology
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
          className="font-heading text-2xl md:text-3xl text-white/90 tracking-tight max-w-2xl"
        >
          Built to connect with the tools your business already uses.
        </motion.h2>
      </div>

      <div>
        {GROUPS.map((group, i) => <Row key={group.label} group={group} index={i} />)}
      </div>
    </section>
  );
}
